/**
 * Vouchers Page — admin: list, generate, revoke voucher codes.
 * Backend: feat/fee-credit-system (PR1, merged) + feat/bet-platform-fee (PR2).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './VouchersPage.css';

interface Voucher {
  id: string;
  code: string;
  partnerId: string | null;
  feeCreditAmount: number;
  feeCreditAmountCents: string;
  currency: string;
  maxRedemptions: number;
  redemptionCount: number;
  perUserLimit: number;
  feeCreditExpiresInDays: number;
  voucherValidUntil: string;
  requiresKyc: boolean;
  minAccountAgeDays: number;
  status: string;
  createdBy: string;
  internalLabel: string | null;
  createdAt: string;
}

const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'GBP'] as const;
type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

interface AffiliatePartnerOption {
  id: string;
  name: string;
  codePrefix: string;
}

const PAGE_SIZE = 50;

const VouchersPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [partners, setPartners] = useState<AffiliatePartnerOption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterPartnerId, setFilterPartnerId] = useState<string>('');
  const [filterLabel, setFilterLabel] = useState<string>('');
  const [filterCurrency, setFilterCurrency] = useState<string>('');
  const [labelInput, setLabelInput] = useState<string>('');         // unconfirmed input; commits on Enter / blur
  const [offset, setOffset] = useState(0);

  const [showGenerate, setShowGenerate] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Voucher | null>(null);
  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: {
        partnerId?: string;
        status?: 'active' | 'paused' | 'expired' | 'revoked' | 'exhausted';
        label?: string;
        currency?: SupportedCurrency;
        limit: number;
        offset: number;
      } = { limit: PAGE_SIZE, offset };
      if (filterStatus) params.status = filterStatus as 'active' | 'paused' | 'expired' | 'revoked' | 'exhausted';
      if (filterPartnerId) params.partnerId = filterPartnerId;
      if (filterLabel) params.label = filterLabel;
      if (filterCurrency) params.currency = filterCurrency as SupportedCurrency;
      const result = await apiService.listVouchers(params);
      setVouchers(result.vouchers);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPartnerId, filterLabel, filterCurrency, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    apiService
      .listAffiliatePartners({ status: 'active', limit: 200, offset: 0 })
      .then((r) => setPartners(r.partners.map((p) => ({ id: p.id, name: p.name, codePrefix: p.codePrefix }))))
      .catch(() => { /* partners list is optional for this page */ });
  }, []);

  const handleRevoke = async (voucher: Voucher, reason: string) => {
    try {
      await apiService.revokeVoucher(voucher.id, reason);
      setSuccess(`Voucher ${voucher.code} revoked`);
      setRevokeTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed');
    }
  };

  return (
    <div className="vouchers-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Vouchers</h1>
          <div className="page-sub">{total} total · prefix VRS = system-issued, others = affiliate partners</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>+ Generate codes</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess(null)}>{success}</div>}

      <div className="card">
        <div className="card-body">
          <div className="filters">
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="exhausted">exhausted</option>
              <option value="expired">expired</option>
              <option value="revoked">revoked</option>
            </select>
            <select value={filterPartnerId} onChange={(e) => { setFilterPartnerId(e.target.value); setOffset(0); }}>
              <option value="">All partners (incl. system VRS)</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.codePrefix})</option>
              ))}
            </select>
            <select value={filterCurrency} onChange={(e) => { setFilterCurrency(e.target.value); setOffset(0); }}>
              <option value="">All currencies</option>
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Filter by label (substring)"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setFilterLabel(labelInput.trim());
                  setOffset(0);
                }
              }}
              onBlur={() => {
                // Commit on blur so users who tab out without pressing Enter still trigger the filter.
                if (labelInput.trim() !== filterLabel) {
                  setFilterLabel(labelInput.trim());
                  setOffset(0);
                }
              }}
              className="filter-input"
            />
            {(filterLabel || filterCurrency || filterStatus || filterPartnerId) && (
              <button
                className="btn btn-small btn-secondary"
                onClick={() => {
                  setFilterStatus('');
                  setFilterPartnerId('');
                  setFilterCurrency('');
                  setFilterLabel('');
                  setLabelInput('');
                  setOffset(0);
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : vouchers.length === 0 ? (
            <div className="empty">No vouchers found for this filter.</div>
          ) : (
            <table className="vouchers-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th>Amount</th>
                  <th>Used / Max</th>
                  <th>Per user</th>
                  <th>Expires (credit)</th>
                  <th>Valid until</th>
                  <th>KYC</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id}>
                    <td><code>{v.code}</code></td>
                    <td className="label-cell" title={v.internalLabel ?? ''}>
                      {v.internalLabel ?? <span className="muted">—</span>}
                    </td>
                    <td>{v.feeCreditAmount.toFixed(2)} {v.currency}</td>
                    <td>{v.redemptionCount} / {v.maxRedemptions}</td>
                    <td>{v.perUserLimit}</td>
                    <td>{v.feeCreditExpiresInDays}d</td>
                    <td>{new Date(v.voucherValidUntil).toLocaleDateString()}</td>
                    <td>{v.requiresKyc ? 'Yes' : 'No'}</td>
                    <td><span className={`status status-${v.status}`}>{v.status}</span></td>
                    <td>{new Date(v.createdAt).toLocaleDateString()}</td>
                    <td>
                      {v.status === 'active' && (
                        <button className="btn btn-small btn-danger" onClick={() => setRevokeTarget(v)}>Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="pagination">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
            <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
            <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </div>
        </div>
      </div>

      {showGenerate && (
        <GenerateVoucherModal
          partners={partners}
          onClose={() => { setShowGenerate(false); setGeneratedCodes(null); }}
          onGenerated={(codes) => {
            setGeneratedCodes(codes);
            setSuccess(`Generated ${codes.length} voucher code${codes.length === 1 ? '' : 's'}`);
            void load();
          }}
          generatedCodes={generatedCodes}
        />
      )}

      {revokeTarget && (
        <RevokeModal
          voucher={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onConfirm={(reason) => handleRevoke(revokeTarget, reason)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Generate modal
// ============================================================================

interface GenerateProps {
  partners: AffiliatePartnerOption[];
  onClose: () => void;
  onGenerated: (codes: string[]) => void;
  generatedCodes: string[] | null;
}

const GenerateVoucherModal: React.FC<GenerateProps> = ({ partners, onClose, onGenerated, generatedCodes }) => {
  const [partnerId, setPartnerId] = useState<string>('');
  const [feeCreditAmount, setFeeCreditAmount] = useState<string>('5.00');
  const [currency, setCurrency] = useState<SupportedCurrency>('EUR');
  const [internalLabel, setInternalLabel] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [validUntilDate, setValidUntilDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [feeCreditExpiresInDays, setFeeCreditExpiresInDays] = useState<number>(90);
  const [perUserLimit, setPerUserLimit] = useState<number>(1);
  const [maxRedemptionsPerCode, setMaxRedemptionsPerCode] = useState<number>(1);
  const [requiresKyc, setRequiresKyc] = useState<boolean>(true);
  const [minAccountAgeDays, setMinAccountAgeDays] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Pre-fill internalLabel with the partner name when a partner is selected.
  // Admin can still edit; suggestion only.
  const selectedPartner = partners.find((p) => p.id === partnerId);
  React.useEffect(() => {
    if (selectedPartner && internalLabel.trim() === '') {
      const today = new Date().toISOString().slice(0, 10);
      setInternalLabel(`${selectedPartner.name} ${today}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const cents = Math.round(parseFloat(feeCreditAmount) * 100);
      if (!Number.isFinite(cents) || cents <= 0) {
        throw new Error('Enter a positive amount');
      }
      const result = await apiService.generateVouchers({
        partnerId: partnerId || null,
        campaignId: null,
        feeCreditAmountCents: String(cents),
        quantity,
        voucherValidUntil: new Date(`${validUntilDate}T23:59:59Z`).toISOString(),
        feeCreditExpiresInDays,
        perUserLimit,
        maxRedemptionsPerCode,
        requiresKyc,
        minAccountAgeDays,
        currency,
        internalLabel: internalLabel.trim() || undefined,
      });
      onGenerated(result.codes);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (generatedCodes) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h2>Generated {generatedCodes.length} code{generatedCodes.length === 1 ? '' : 's'}</h2>
          <p className="modal-sub">Copy these codes now — they're listed in the table below as well.</p>
          <textarea readOnly className="codes-output" value={generatedCodes.join('\n')} />
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => navigator.clipboard.writeText(generatedCodes.join('\n'))}>
              Copy all
            </button>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Generate voucher codes</h2>
        {err && <div className="alert alert-error">{err}</div>}
        <div className="form-grid">
          <label>
            <span>Partner</span>
            <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">System (VRS prefix)</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.codePrefix})</option>
              ))}
            </select>
          </label>
          <label>
            <span>Currency</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}>
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Amount per code ({currency})</span>
            <input type="text" inputMode="decimal" value={feeCreditAmount} onChange={(e) => setFeeCreditAmount(e.target.value)} />
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch', gridColumn: '1 / span 2' }}>
            <span>Internal label (filterable; partner / campaign / promo tag)</span>
            <input
              type="text"
              maxLength={200}
              value={internalLabel}
              onChange={(e) => setInternalLabel(e.target.value)}
              placeholder={selectedPartner ? `e.g. ${selectedPartner.name} April-2026` : 'e.g. Comp ticket 1234, Waitlist batch May'}
            />
          </label>
          <label>
            <span>Quantity</span>
            <input type="number" min={1} max={10000} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
          </label>
          <label>
            <span>Voucher valid until</span>
            <input type="date" value={validUntilDate} onChange={(e) => setValidUntilDate(e.target.value)} />
          </label>
          <label>
            <span>Fee credit expires (days after redeem)</span>
            <input type="number" min={1} max={3650} value={feeCreditExpiresInDays} onChange={(e) => setFeeCreditExpiresInDays(Number(e.target.value))} />
          </label>
          <label>
            <span>Per-user limit</span>
            <input type="number" min={1} value={perUserLimit} onChange={(e) => setPerUserLimit(Number(e.target.value))} />
          </label>
          <label>
            <span>Max redemptions per code</span>
            <input type="number" min={1} value={maxRedemptionsPerCode} onChange={(e) => setMaxRedemptionsPerCode(Number(e.target.value))} />
          </label>
          <label>
            <span>Min account age (days)</span>
            <input type="number" min={0} value={minAccountAgeDays} onChange={(e) => setMinAccountAgeDays(Number(e.target.value))} />
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={requiresKyc} onChange={(e) => setRequiresKyc(e.target.checked)} />
            <span>Requires KYC (recommended)</span>
          </label>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Generating…' : `Generate ${quantity} code${quantity === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Revoke modal
// ============================================================================

const RevokeModal: React.FC<{ voucher: Voucher; onClose: () => void; onConfirm: (reason: string) => void }> = ({
  voucher,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-confirm" onClick={(e) => e.stopPropagation()}>
        <h2>Revoke voucher {voucher.code}</h2>
        <p>
          This blocks all future redemptions of <code>{voucher.code}</code>.
          Already-redeemed grants are NOT clawed back.
        </p>
        <textarea
          placeholder="Reason (audit trail) — required, min 3 chars"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="reason-input"
        />
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-danger"
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason)}
          >
            Revoke
          </button>
        </div>
      </div>
    </div>
  );
};

export default VouchersPage;
